// src/api/features/promos/queries.ts

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { listPromos, getPromo, getPromoAnalytics, searchPromoCustomers } from "./api";
import type { PromoListQuery, PaginatedPromos, PromoWithConfig, PromoAnalytics, PromoCustomerLite } from "./types";

export const PromosKeys = {
  all: ["promos"] as const,
  list: (query: PromoListQuery = {}) => ["promos", "list", query] as const,
  detail: (id: number | string) => ["promos", "detail", String(id)] as const,
  analytics: (id: number | string) => ["promos", "analytics", String(id)] as const,
};

export function usePromosList(query: PromoListQuery = {}, options?: { enabled?: boolean }) {
  return useQuery<PaginatedPromos, Error>({
    queryKey: PromosKeys.list(query),
    queryFn: () => listPromos(query),
    enabled: options?.enabled ?? true,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

export function usePromoDetail(id: number | null) {
  const enabled = id != null;
  return useQuery<PromoWithConfig, Error>({
    queryKey: enabled ? PromosKeys.detail(id as number) : ["promos", "detail", "disabled"],
    queryFn: () => {
      if (id == null) return Promise.reject(new Error("Promo id is required"));
      return getPromo(id);
    },
    enabled,
    staleTime: 30_000,
  });
}

export function usePromoCustomerSearch(q: string, options?: { enabled?: boolean }) {
  const term = q.trim();
  return useQuery<PromoCustomerLite[], Error>({
    queryKey: ["promos", "customer-search", term],
    queryFn: () => searchPromoCustomers(term),
    enabled: (options?.enabled ?? true) && term.length >= 1,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function usePromoAnalytics(id: number | null) {
  const enabled = id != null;
  return useQuery<PromoAnalytics, Error>({
    queryKey: enabled ? PromosKeys.analytics(id as number) : ["promos", "analytics", "disabled"],
    queryFn: () => {
      if (id == null) return Promise.reject(new Error("Promo id is required"));
      return getPromoAnalytics(id);
    },
    enabled,
    staleTime: 15_000,
  });
}
